import React, {useState} from 'react';
import Title from '../../../../components/AccountSettings/Title';
import EditableTable from 'src/views/admin/EditableTable';
import {ColumnSettings} from '../../../../views/admin/EditableTable/types';
import AllowedDomainService from 'src/services/AllowedDomainService';
import DnsSettingsModal from 'src/apps/AdminUniversity/dns-settings-modal/dns-settings-modal';
import Modal from 'shared/components/modal';
import StatefulCheckbox from 'shared/components/stateful-checkbox';
import CheckedValue from 'src/views/admin/CheckedValue';
import TextButton from 'src/components/TextButton';
import styles from './styles.module.scss';

export const DOMAIN_SEND_TOOLTIP =
  'If enabled, email addresses created under this domain can be used as the sender address and can also receive replies.';

export const EMAIL_ALIAS_TOOLTIP =
  'If enabled, email addresses created under this domain can be used as reply-to addresses only. Emails cannot be sent from these addresses.';

interface Props {
  universityId: string;
}

interface DomainItem {
  id: string;
  domain?: string;
  fromEmailAlias?: boolean;
  verifiedDomainSend?: boolean;
  sesVerificationToken?: string;
  domainVerifiedAt?: string;
  sesDkimTokens?: string[];
}

interface DeleteConfirmation {
  item: {id: string; domain: string};
  resolve: (value: boolean) => void;
}

const ManageAllowedEmailDomains = ({universityId}: Props) => {
  const [dnsConfig, setDnsConfig] = useState<DomainItem | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
  const [updatedItems, setUpdatedItems] = useState<Record<string, Partial<DomainItem>>>({});

  const updateItem = (id: string, updates: Partial<DomainItem>) => {
    setUpdatedItems((prev) => ({...prev, [id]: {...prev[id], ...updates}}));
  };

  const handleAliasToggle = async (value: boolean, item: DomainItem) => {
    await AllowedDomainService.update(
      item.id,
      {fromEmailAlias: value},
      {university_id: universityId}
    );
    updateItem(item.id, {fromEmailAlias: value});
  };

  const handleSenderToggle = async (value: boolean, item: DomainItem) => {
    const response = await AllowedDomainService.update(
      item.id,
      {verifiedDomainSend: value},
      {university_id: universityId}
    );

    updateItem(item.id, {
      verifiedDomainSend: value,
      sesVerificationToken: response.sesVerificationToken,
      sesDkimTokens: response.sesDkimTokens,
    });

    if (value) {
      setDnsConfig({
        id: response._id,
        domain: response.domain,
        sesVerificationToken: response.sesVerificationToken,
        sesDkimTokens: response.sesDkimTokens,
      });
    }
  };

  const columns: ColumnSettings[] = [
    {
      name: 'domain',
      title: 'Email Domain',
      editable: false,
      editableOnCreate: true,
    },
    {
      name: 'domainVerifiedAt',
      title: 'DNS Verified?',
      editable: false,
      renderValue: (value, item) => {
        const mergedItem = {...item, ...updatedItems[item.id]};
        if (!mergedItem.verifiedDomainSend) {
          return <span>N/A</span>;
        }
        return CheckedValue(value);
      },
    },
    {
      name: 'verifiedDomainSend',
      title: 'DNS',
      editable: false,
      tooltip: DOMAIN_SEND_TOOLTIP,
      renderValue: (value, item) => (
        <div className={styles.checkboxSection}>
          <StatefulCheckbox
            initialChecked={!!value}
            onToggle={(newValue) => handleSenderToggle(newValue, item)}
          />
        </div>
      ),
    },
    {
      name: 'fromEmailAlias',
      title: 'Alias',
      editable: false,
      tooltip: EMAIL_ALIAS_TOOLTIP,
      renderValue: (value, item) => (
        <div className={styles.checkboxSection}>
          <StatefulCheckbox
            initialChecked={!!value}
            onToggle={(newValue) => handleAliasToggle(newValue, item)}
          />
        </div>
      ),
    },
    {
      name: 'dnsDetails',
      title: 'DNS Details',
      editable: false,
      renderValue: (_, item) => {
        const mergedItem = {...item, ...updatedItems[item.id]};
        if (!mergedItem.verifiedDomainSend) {
          return <span>N/A</span>;
        }
        return (
          <TextButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDnsConfig({
                id: String(mergedItem.id),
                domain: String(mergedItem.domain),
                sesVerificationToken: String(mergedItem.sesVerificationToken),
                sesDkimTokens: mergedItem.sesDkimTokens as unknown as string[],
              });
            }}
          >
            View DNS Details
          </TextButton>
        );
      },
    },
  ];

  return (
    <div className="account-settings-form" data-testid="manage-allowed-email-domains">
      <Title>Manage Email Messaging Domains</Title>
      <EditableTable
        basePath={`/v1/admin/allowed-email-domains?university_id=${universityId}`}
        defaultSortKey="domain"
        columns={columns}
        identifier="allowed-email-domains"
        defaultValues={{universityId: universityId}}
        disableUpdating
        confirmDelete={async (item) =>
          new Promise<boolean>((resolve) => {
            setDeleteConfirmation({
              item: {
                id: item.id,
                domain: item.domain as string,
              },
              resolve,
            });
          })
        }
      />
      <DnsSettingsModal
        show={!!dnsConfig}
        onDismiss={() => setDnsConfig(null)}
        dnsConfig={dnsConfig}
      />
      {deleteConfirmation && (
        <Modal
          show
          onClose={() => {
            deleteConfirmation.resolve(false);
            setDeleteConfirmation(null);
          }}
        >
          <Modal.TextHeader
            onClose={() => {
              deleteConfirmation.resolve(false);
              setDeleteConfirmation(null);
            }}
          >
            Delete Confirmation
          </Modal.TextHeader>
          <Modal.Body>
            Are you sure you want to delete the following email domain?
            <p style={{fontWeight: 'bold', marginTop: '12px'}}>{deleteConfirmation.item.domain}</p>
          </Modal.Body>
          <Modal.Footer
            primaryButton={{
              text: 'Delete',
              color: 'red',
              onClick: () => {
                deleteConfirmation.resolve(true);
                setDeleteConfirmation(null);
              },
            }}
            secondaryButton={{
              text: 'Cancel',
              onClick: () => {
                deleteConfirmation.resolve(false);
                setDeleteConfirmation(null);
              },
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default ManageAllowedEmailDomains;
